/**
 * The SearchBox view allows to create search control on a map.
 *
 * The tomtom.searchBox() method creates a new instance of SearchBox component.
 *
 * @class searchBox
 */
define('widgets/controls/searchBox',["display/map", "services/search/fuzzySearch", "services/reverseGeocode/reverseGeocode", "widgets/controls/location", "services/utils"],
    function (L, searchService, reverseGeocodeService, location, utils) {
        "use strict";

        var ENTER = 13, ARROWUP = 38, ARROWDOWN = 40,
            EVENTS = {
                ResultCreated: "resultCreated",
                ResultClicked: "resultClicked",
                ClearButtonClicked: "clearButtonClicked",
                ResultsCleared: "resultsCleared",
                ResultsFound: "resultsFound",
                ResultsDrawn: "resultsDrawn",
                SearchInputChanged: "searchInputChanged",
                LocationError: "locationError"
            };

        function searchBoxEvent(element, data, text) {
            return {element: element, data: data, text: text};
        }

        function locationError(control) {
            return function (error) {
                control.input.value = "";
                control.input.focus();
                control.clearResultsContainer();
                createResult(control);
                control.fire(EVENTS.LocationError, searchBoxEvent(control.input, error, error.message));
                console.error(error);
            };
        }

        function locationSuccess(control) {
            return function (location) {
                var options = utils.clone(control.options.serviceOptions);
                reverseGeocodeService(options).position(location.latlng).go(function (result) {
                    result.position = location.latlng;
                    control.clearResultsContainer();
                    control.input.value = resolveResultText(result);
                    control.input.focus();
                    control.fire(EVENTS.ResultClicked, searchBoxEvent(control.locationIcon, result, control.input.value));
                });
            };
        }

        function createCurrentLocationButton(control) {
            control.location = location({size: null});
            control.locationIcon = control.location.onAdd(control._map);
            control.inputContainer.appendChild(control.locationIcon);
            control.location.on(control.location.Event.LocationFound, locationSuccess(control));
            control.location.on(control.location.Event.LocationError, locationError(control));
        }

        function createDOMElements(control) {
            var container = new L.DomUtil.create("div", "leaflet-control-search");
            control._container = container;
            control.inputContainer = new L.DomUtil.create("div", "leaflet-control-search-input-container", container);
            control.input = new L.DomUtil.create("input", control.options.icon + " leaflet-control-search-input", control.inputContainer);
            control.input.required = true;
            control.clearButton = new L.DomUtil.create("div", "leaflet-control-search-input-close-icon icon-close-gray", control.inputContainer);
            control.resultsContainer = new L.DomUtil.create("div", "leaflet-control-search-results-container", container);
            if (control.options.location) {
                createCurrentLocationButton(control);
            }
            if (control.options.id) {
                control.input.id = control.options.id;
            }
            setHandlers(control, container);
            control.input.setAttribute("placeholder", control.options.placeholder);
            return container;
        }

        function clearSearchBox() {
            this.input.focus();
            this.input.value = "";
            this.clearResultsContainer();
            this.fire(EVENTS.ClearButtonClicked, searchBoxEvent(this.clearButton));
        }

        function setHandlers(control, container) {
            L.DomEvent.disableClickPropagation(container);
            L.DomEvent.disableScrollPropagation(container);
            L.DomEvent.on(container, "drag", L.DomEvent.stopPropagation);
            L.DomEvent.on(container, "keydown", onKeyDown, control);
            L.DomEvent.on(container, "input", onInputChange(control), control);
            L.DomEvent.on(control._map, "dragend", onMapEndDrag, control);
            L.DomEvent.on(control.clearButton, "click", clearSearchBox, control);
            if (control.options.collapsible) {
                L.DomEvent.on(control.input, "focus", control.expand, control);
                L.DomEvent.on(control.input, "blur", control.collapse, control);
                if (control.options.collapsed === true) {
                    control.collapse();
                }
            }
        }

        function createResult(control, resultData) {
            var result = L.DomUtil.create("div", "leaflet-control-search-result", control.resultsContainer);
            result.innerHTML = resolveResultText(resultData) || "No results found.";
            control.resultsContainer.style.height = 'auto';
            highlightWords(result, control.input.value);
            control.fire(EVENTS.ResultCreated, searchBoxEvent(result, resultData));
            L.DomEvent.on(result, "click", function () {
                onResultSelect.call(control, resultData);
            }, control);
            L.DomEvent.on(result, "mouseover", function () {
                this.stopSearch = true;
            }, control);
        }

        function highlightWords(element, enteredText) {
            var words = enteredText.split(/[- ,]+/),
                regex = new RegExp("(" + words.join("|") + ")", "ig"),
                highlighted = element.innerHTML;
            highlighted = highlighted.replace(regex, "<span class=\"leaflet-control-search-result-highlight\">$1</span>");
            element.innerHTML = highlighted;
        }

        function handleResults(results) {
            this.clearResultsContainer();
            this.expand();
            this.fire(EVENTS.ResultsFound, searchBoxEvent(this.resultsContainer, results));
            this._searchResponses = results;
            if (!results || results.length === 0) {
                createResult(this);
                return;
            }
            for (var i = 0; i < results.length; i += 1) {
                createResult(this, results[i]);
            }
            this.resultsContainer.style.height = 'auto';
            this.fire(EVENTS.ResultsDrawn, searchBoxEvent(this.resultsContainer, results));
        }

        function onResultSelect(resultData) {
            if (!resultData) {
                return;
            }
            this.stopSearch = true;
            this.input.value = resolveResultText(resultData);
            this.clearResultsContainer();
            this.input.blur();
            this.fire(EVENTS.ResultClicked, searchBoxEvent(this.input, resultData, this.input.value));
        }

        function resolveResultText(resultData) {
            if (!resultData || !resultData.address) {
                return resultData;
            }
            return resultData.poi ? resultData.poi.name + ", " + resultData.address.freeformAddress : resultData.address.freeformAddress;
        }

        function onInputChange(control) {
            var inputWaiting = false, throttle = false, ignorePrompt = false, lastCallTime, timerId;

            function performPromptSearch() {
                var currentCallTime = Date.now();
                if (control.input.value.length === 0) {
                    return;
                }
                lastCallTime = currentCallTime;
                if (inputWaiting) {
                    control.lastSearchText = "";
                    searchService(getOptionsForSearch(control, true)).go(function (result) {
                        if (!ignorePrompt && currentCallTime === lastCallTime) {
                            handleResults.call(control, result);
                        }
                    });
                    setTimeout(performPromptSearch, control.options.throttleTimePrompt);
                    inputWaiting = false;
                    throttle = true;
                } else {
                    throttle = false;
                }
            }

            function performSearch() {
                ignorePrompt = true;
                throttle = true;
                inputWaiting = false;
                if (control.input.value.length === 0 || control.lastSearchText === control.input.value || control.stopSearch) {
                    return;
                }
                control.lastSearchText = control.input.value;
                searchService(getOptionsForSearch(control)).go(function (result) {
                    if (!control.stopSearch) {
                        handleResults.call(control, result);
                    }
                    throttle = false;
                });
            }

            return function () {
                this.fire(EVENTS.SearchInputChanged, searchBoxEvent(this.input, this.input.value, this.input.value));
                control.stopSearch = false;

                if (timerId) {
                    clearTimeout(timerId);
                }
                timerId = setTimeout(performSearch, control.options.idleTimeKeyPress);
                inputWaiting = this.input.value.length > 0;

                if (this.options.withPrompt && !throttle && this.input.value.length >= this.options.minNumberCharsPrompt) {
                    ignorePrompt = false;
                    performPromptSearch();
                }
            };
        }

        function onMapEndDrag() {
            if (this._searchResponses) {
                this.search(this.input.value);
            }
        }

        function onKeyDown(event) {
            var code = event.keyCode || event.which,
                selectedResult = this.resultsContainer.getElementsByClassName("highlight")[0];

            if (code === ENTER && this.input.value) {
                if (selectedResult) {
                    var selectedResultIndex = [].indexOf.call(this.resultsContainer.children, selectedResult);
                    onResultSelect.call(this, this._searchResponses[selectedResultIndex]);
                } else {
                    this.search(this.input.value);
                }
            }

            if (this.resultsContainer.hasChildNodes()) {
                if (code === ARROWUP || code === ARROWDOWN) {
                    this.stopSearch = true;
                    highlightNextResult.call(this, code, selectedResult, this.resultsContainer);
                }
            }
        }

        function highlightNextResult(keyCode, selectedElement, resultsContainerElement) {
            var highlightClass = "highlight",
                rules = {
                    38: {
                        edgeCase: "firstChild",
                        nextSibling: "previousElementSibling",
                        nextAfterEdgeCase: "lastChild"
                    },
                    40: {
                        edgeCase: "lastChild",
                        nextSibling: "nextElementSibling",
                        nextAfterEdgeCase: "firstChild"
                    }
                },
                newlySelectedElement;
            if (selectedElement) {
                selectedElement.className = selectedElement.className.replace(highlightClass, "");
            }

            if (!selectedElement || selectedElement === resultsContainerElement[rules[keyCode].edgeCase]) {
                newlySelectedElement = resultsContainerElement[rules[keyCode].nextAfterEdgeCase];
            } else {
                newlySelectedElement = selectedElement[rules[keyCode].nextSibling];
            }

            newlySelectedElement.className += " " + highlightClass;
        }

        function getOptionsForSearch(control, typeahead) {
            var options = utils.clone(control.options.serviceOptions);
            options.query = control.input.value;
            if (control.options.viewportBoundingBox) {
                options.boundingBox = control._map.getBounds();
            } else {
                options.center = control._map.getCenter();
            }
            if (control.options.disablePOIs) {
                options.idxSet = "PAD,Str,Xstr,Geo,Addr";
            }
            options.typeahead = !!typeahead;
            return options;
        }

        var SearchBox = L.Control.extend({

            includes: L.Mixin.Events,

            options: {
                placeholder: "Search for place...",
                id: false,
                collapsible: true,
                collapsed: false,
                location: false,
                icon: "icon-search",
                disablePOIs: false,
                viewportBoundingBox: false,
                minNumberCharsPrompt: 3,
                throttleTimePrompt: 1000,
                idleTimeKeyPress: 400,
                serviceOptions: {},
                position: 'topleft',
                withPrompt: true
            },
            initialize: function (options) {
                L.Util.setOptions(this, options);
            },
            onAdd: function () {
                return createDOMElements(this);
            },
            onRemove: function () {
                this.clearAllEventListeners();
            },
            /**
             * Method used to clear container of search call results.
             *
             * @method clearResultsContainer
             */
            clearResultsContainer: function () {
                if (this.resultsContainer.hasChildNodes()) {
                    this.resultsContainer.style.height = '0px';
                    this.resultsContainer.innerHTML = "";
                    this._searchResponses = undefined;
                    this.fire(EVENTS.ResultsCleared, searchBoxEvent(this.resultsContainer));
                }
            },
            /**
             * Method used to set data into searchBox without calling a service.
             *
             * @method setResultData
             * @param {Object} resultData Result data given from search service. Must contain address and/or POI
             *     information.
             *
             *  @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             *
             * var resultData = {
             *                    address: {freeformAddress: "Amsterdam"},
             *                    poi: { name: "TomTom, Oosterdoksstraat, Amsterdam, The Netherlands" }
             *                  }
             * searchBoxInstance.setResultData(resultData);
             * ```
             */
            setResultData: function (resultData) {
                if (resultData) {
                    this.input.value = resolveResultText(resultData);
                    this.clearResultsContainer();
                    this.input.blur();
                }
            },
            /**
             * Method used to call a search service with given data.
             *
             * @method search
             * @param {Object} text String object or reverse geocoding object (with query parameter) as a search
             *     query.
             *
             *  @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.search("Amsterdam");
             *
             * var reversedGeocodeResponse = {query: "Amsterdam"}
             * searchBoxInstance.search(reversedGeocodeResponse);
             *
             * ```
             */
            search: function (text) {
                text = text.query || text;
                if (typeof text !== "string" || text.length === 0) {
                    return;
                }
                this.input.focus();
                this.input.value = text;
                this.lastSearchText = text;
                var searchBox = this;
                searchService(getOptionsForSearch(searchBox)).go(function (results) {
                    handleResults.call(searchBox, results);
                });
            },
            /**
             * Method used to collapse search. Collapsing is possible only if search is empty.
             *
             * @method collapse
             */
            collapse: function () {
                if (!this.input.value) {
                    this._container.className += " collapsed";
                    this.input.readOnly = true;
                    this.clearResultsContainer();
                } else {
                    this.expand();
                }
            },
            /**
             * Method used to expand collapsed search.
             *
             * @method expand
             */
            expand: function () {
                this._container.className = this._container.className.replace(/\b collapsed\b/, "");
                this.input.readOnly = false;
            },
            /**
             * Method used to get main container of a searchBox.
             *
             * @method getContainer
             *
             * @return {Node} HTML element representing container of a searchBox.
             */
            getContainer: function () {
                return this._container;
            },
            /**
             * Method used to get location element (if set and added).
             *
             * @method getLocation
             *
             * @return {Node} HTML element representing location element.
             */
            getLocation: function () {
                return this.location;
            },
            /**
             * Method used to set icon by styling search box input.
             *
             * @method setIcon
             * @param className Name of the CSS class that sets _background-image_ property for search box input.
             */
            setIcon: function (className) {
                if (typeof className === "string") {
                    this.input.className = this.input.className.replace(/(icon)((-)(\w+))*/, className);
                }
            },
            /**
             * Method used to add location icon.
             *
             * @method addLocationIcon
             */
            addLocationIcon: function () {
                if (this.location === undefined) {
                    this.options.location = true;
                    createCurrentLocationButton(this);
                }
            },
            /**
             * Method used to remove location icon.
             *
             * @method removeLocationIcon
             */
            removeLocationIcon: function () {
                if (this.location !== undefined) {
                    this.options.location = false;
                    this.locationIcon.parentNode.removeChild(this.locationIcon);
                    this.location.clearAllEventListeners();
                    this.location = undefined;
                }
            },
            /**
             * Whenever the input within the search box is changed by the user this event is triggered.
             *
             * @event SearchInputChanged
             * @param {Object} data The current text entered by the user.
             * @param {String} text Same as data.
             * @param {String} type Event's name, equal to "searchInputChanged".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.SearchInputChanged, function (eventObject) {
             *      console.log(eventObject.text);
             * });
             * ```
             */
            /**
             * This event appears when response from search service is being processed. For each result which is being
             * rendered on the prompt list such event is fired.
             *
             * @event ResultCreated
             * @param {Object} data The result from search service response that is being processed.
             * @param {String} type Event's name, equal to "resultCreated".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultCreated, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when user selects the result from list or when current location is identified.
             *
             * @event ResultClicked
             * @param {Object} data Selected result object from search service response.
             * @param {String} text The human readable string representation of selected result.
             * @param {String} type Event's name, equal to "resultClicked".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultClicked, function (eventObject) {
             *      console.log(eventObject.text);
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when the response from search service is already processed and rendered in a form
             * of list of possible results.
             *
             * @event ResultsDrawn
             * @param {Array} data Array of search service results. Each result has, among other parameters, its
             *     _type_, _address_ and _position_.
             * @param {String} type Event's name, equal to "resultsDrawn".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultsDrawn, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when the response from search service appears for both normal and prompt query.
             *
             * @event ResultsFound
             * @param {Array} data Array of search service results. Each result has, among other parameters, its
             *     _type_, _address_ and _position_.
             * @param {String} type Event's name, equal to "resultsFound".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ResultsFound, function (eventObject) {
             *      console.log(eventObject.data);
             * });
             * ```
             */
            /**
             * Event that is fired when user clicks the clear search box input button.
             *
             * @event ClearButtonClicked
             * @param {String} type Event's name, equal to "clearButtonClicked".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.ClearButtonClicked, function () {
             *      console.log("User clicked the clear button");
             * });
             * ```
             */
            /**
             * Event that is fired when prompt results list is being cleared.
             *
             * @event ResultsCleared
             * @param {String} type Event's name, equal to "resultsCleared".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.LocationsCleared, function () {
             *      console.log("Results were cleared");
             * });
             * ```
             */
            /**
             * Event that is fired when obtaining user's current location fails.
             *
             * @event LocationError
             * @param {Object} eventObject All failure information.
             * @param {String} type Event's name, equal to "locationError".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var searchBoxInstance = tomtom.searchBox().addTo(map);
             * searchBoxInstance.on(searchBoxInstance.Event.LocationError, function (eventObject) {
             *      console.log(eventObject);
             * });
             * ```
             */
            Event: EVENTS
        });

        /**
         * The method to instantiates the searchBox control.
         *
         * @method searchBox
         * @param {Object} [options] Options to be passed to the searchBox control.
         * @param {String} [options.placeholder="Search for place..."] The placeholder text to be displayed inside
         *     searchBox.
         * @param {String} [options.id] Id of a searchBox's DOM element.
         * @param {Boolean} [options.collapsed=false] Flag if checkbox is collapsed on initialization.
         * @param {Boolean} [options.collapsible=true] Flag if searchBox can be collapsed.
         * @param {Boolean} [options.location=false] Display location button to search for current location.
         * @param {String} [options.icon="icon-search"] Determines icon of the searchBox.
         * @param {Boolean} [options.disablePOIs=false] Determines whether search results should not contain POIs.
         * @param {Boolean} [options.viewportBoundingBox=false] Enables or disables searching on a currently visible
         *     map area. If unset, search service will use current map center.
         * @param {Boolean} [options.withPrompt=true] Should widget show user prompts while typing, the call will have
         *     typeahead flag and will be throttled according to the following settings.
         * @param {Integer} [options.minNumberCharsPrompt=3] Sets the minimal required number of characters that are
         *     required to perform search.
         * @param {Integer} [options.throttleTimePrompt=1000] Sets the minimal time between the following search
         *     service calls when user types text into the input.
         * @param {Integer} [options.idleTimeKeyPress=400] Sets the time between following key press events to assume
         *     the user finished typing the query.
         * @param {Object} [options.serviceOptions] Options to be passed to service. See search service options.
         *
         * @example
         * ```javascript
         *
         *  // SearchBox with location button
         *  var options = {
         *      location: true
         *  };
         *
         * var searchBoxInstance = searchBox(options).addTo(map);
         *
         * ```
         */
        return function (options) {
            return new SearchBox(options);
        };
    });
